import { AvatarGroup, Box, Button, Typography, useColorScheme, useTheme } from "@mui/material";
import StarsFilled from "./assets/StarsFilled";
import StarsOutlined from "./assets/StarsOutlined";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import CustomAvatar from "../../component/CustomAvatar";
import Avatar_1 from "./assets/Avatar_1.png";
import Avatar_2 from "./assets/Avatar_2.png";
import Avatar_3 from "./assets/Avatar_3.png";
import Avatar_4 from "./assets/Avatar_4.png";
import { USER_TYPE } from "../../config/const";
import { useQuery } from "@tanstack/react-query";
import { getSortedCreatorList } from "../../utils/api";
import HeroVisual from "./HeroVisual";

const ValueProp: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, systemMode } = useColorScheme();
  const { authUser, setLoginModalVisible } = useAuth();

  const isLightMode = ( mode === 'light' || (mode === 'system' && systemMode === 'light'));

  const { data, isPending } = useQuery({
    queryKey: ['creator-count'],
    queryFn: () => getSortedCreatorList(authUser?.uid, 1, 1)
                    .then((res) => {if (res.ok) return res.json()})
                    .then((datum) => datum?.total_count || 0)
                    .catch((e) => 0)
  })

  return (
    <Box
      sx={[{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        pb: {xs: 3, md: 0},
      },
      isLightMode ? {
        backgroundColor: {xs: 'none', md: theme.palette.background["container-1"]},
      } : {
        background: {xs: 'none', md: 'linear-gradient(135deg, #00071200 30%, #0A3C8F94, #0040AB, #000E25 75%)'},
      }
      ]}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: {xs: "column-reverse", md: "row"},
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: 'fit-content',
          px: {xs: 2.5, md: 4},
          gap: {xs: 0, md: 4},
          maxWidth: "lg",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: {xs: "center", md: "flex-start"},
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              px: 2,
              py: 1,
              alignItems: 'center',
              borderRadius: 4,
              backgroundColor: theme.palette.background["container-brand-2"]
            }}
          >
            <StarsOutlined
              color={theme.palette.primary.main}
            />
            <Typography
              variant="body3"
              sx={{
                color: theme => theme.palette.primary.main
              }}
            >
              Try free today - no credit card required
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: {xs: "center", md: "left"},
              textAlign: {xs: "center", md: "left"},
              width: "100%",
              maxWidth: {xs: "none", md: "330px"},
              gap: 1,
            }}
          >
            <Typography variant="h4">
              Turn your{' '}
              <span style={{ color: theme.palette.primary.main }}>expertise</span>
              {' '}into an{' '}
              <span style={{ color: theme.palette.primary.main }}>AI persona</span>
            </Typography>
            <Typography variant="body1" color="text.secondary" >
              Soopra trains a smart, on-brand AI version of yourself that engages
              your audience, promotes your work, and generates passive income.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              width: {xs: "100%", md: "fit-content"},
            }}
          >
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<StarsFilled
                color={theme.palette.primary.contrastText}
              />}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (authUser) {
                  if (authUser.userType === USER_TYPE.CREATOR) {
                    navigate(`/${authUser.userName}`)
                  } else {
                    navigate('/creator-onboarding')
                  }
                } else {
                  setLoginModalVisible({
                    isOpen: true,
                    formType: 'signup',
                    currentStep: 1,
                  })
                }
              }}
              href={authUser ? '/creator-onboarding' : null}
            >
              Create my AI persona
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              fullWidth
              onClick={() => {
                navigate('/creators')
              }}
              href='/creators'
              style={{
                background: theme.palette.primary._states.focus,
              }}
            >
              Explore Experts
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: {xs: "center", md: "flex-start"},
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <AvatarGroup spacing={14}>
              <CustomAvatar src={Avatar_1} sx={{width: 32, height: 32, border: 4}}/>
              <CustomAvatar src={Avatar_2} sx={{width: 32, height: 32, border: 4}}/>
              <CustomAvatar src={Avatar_3} sx={{width: 32, height: 32, border: 4}}/>
              <CustomAvatar src={Avatar_4} sx={{width: 32, height: 32, border: 4}}/>
            </AvatarGroup>
            <Typography variant="body2" color="text.secondary">
              {`Join ${(!isPending && data) ? Math.floor(data/100)*100: 600}+ AI Personas`}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            p: 0,
            m: 0,
            maxWidth: "100%",
            height: "100%",
            display: 'flex',
            alignItems: 'flex-end',
            flex: 1,
          }}
        >
          <HeroVisual />
        </Box>
      </Box>
    </Box>
  )
}

export default ValueProp;
